import dayjs from "dayjs";
import React from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { HolidayButton } from "./HolidayButton";
import { useUpcomingHolidays } from "./hooks/useUpcomingHolidays";
import { HookFormSpecialOperationHoursIndividual } from "./Individual";
import { SpecialHourPeriod, SpecialOperationHours } from "./types";
import { HookFormError } from "../Error";

import "./styles.scss";

export const HookFormSpecialOperationHours = () => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `facilityDetailsPage.modalEditProfile.editProfileForm.specialHours.${key}`
    );

  const { control, watch, formState } = useFormContext<SpecialOperationHours>();

  // FIXME: Either Zod or React Hook Form don't seem to want to attach errors to an array field,
  // and the error shifts between these two places.
  const error =
    formState.errors.specialHourPeriods?.message ??
    formState.errors.specialHourPeriods?.root?.message ??
    undefined;

  const specialHourPeriods = useFieldArray({
    control,
    name: "specialHourPeriods",
  });

  const selectedDates = watch("specialHourPeriods").map(
    (field: SpecialHourPeriod) => field.startDate
  );

  const handleAddSpecialHourPeriod = (value?: Partial<SpecialHourPeriod>) => {
    const newPeriod = {
      startDate: value?.startDate ?? dayjs().startOf("day").toDate(),
      periods: value?.periods ?? [
        {
          openTime: { hours: 0, minutes: 0 },
          closeTime: { hours: 0, minutes: 0 },
        },
      ],
      closed: value?.closed ?? false,
    };
    specialHourPeriods.append(newPeriod);
  };

  const handleCopySpecialHourPeriod = (index: number) => {
    const value = watch(`specialHourPeriods.${index}`);
    handleAddSpecialHourPeriod(value);
  };

  const holidays = useUpcomingHolidays();

  const handleAddHoliday = (startDate: Date) => {
    handleAddSpecialHourPeriod({ startDate });
  };

  return (
    <div className="special-operation-hours">
      <p className="special-operation-hours-explanation">{t("explanation")}</p>
      <div className="special-operation-hours-current">
        <h3>{t("current.title")}</h3>
        {specialHourPeriods.fields.length < 1 && (
          <span>{t("no_special_hours")}</span>
        )}
        {specialHourPeriods.fields.map((field, index) => (
          <div className="special-operation-hours-current-item" key={field.id}>
            <HookFormSpecialOperationHoursIndividual
              holidays={holidays}
              dateIndex={index}
            />
            <div className="special-operation-hours-current-item-buttons">
              <button
                type="button"
                className="btn copy"
                onClick={() => handleCopySpecialHourPeriod(index)}
              ></button>
              <button
                type="button"
                className="btn trash"
                onClick={() => specialHourPeriods.remove(index)}
              ></button>
            </div>
          </div>
        ))}
        <HookFormError error={error} />
        <div className="special-operation-hours-controls">
          <button
            type="button"
            className="btn btn-border"
            onClick={() => handleAddSpecialHourPeriod()}
          >
            {t("add_date")}
          </button>
          <div className="special-operation-hours-upcoming-holidays-container">
            <h3>{t("upcoming_holidays")}</h3>
            <div className="special-operation-hours-upcoming-holidays">
              {holidays?.map((holiday) => (
                <HolidayButton
                  key={`${holiday.date}-${holiday.name}`}
                  holiday={holiday}
                  onClick={handleAddHoliday}
                  selectedDates={selectedDates}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
